<template>
    <div id="tags-list" class="container-fluid main-content page-content">
        <div class="page-header">
            <div>
                <h3 class="page-title">{{ $t('tags') }}</h3>
            </div>
        </div>
        <div class="page-card">
             <div class="row justify-content-between px-3 py-1">
                <div class="col-6">
                    <div class="position-relative">
                        <input
                            type="text"
                            class="form-control fs-13 py-1 pr-4"
                            v-bind:placeholder="$t('search')"
                            v-model="search"
                        >

                        <span
                            v-if="search"
                            class="clear-search"
                            @click="search = ''"
                        >
                            &times;
                        </span>
                    </div>
                </div>
                <div class="col-6 row">
                    <div class="col-9 text-right pr-0">
                        <input
                            type="text"
                            class="form-control text-small py-1 pr-0"
                            v-bind:placeholder="$t('add-tags')"
                            v-model=newTag
                            v-on:keyup.enter="addTag()"
                            :disabled="search !== '' "
                        >
                    </div>
                    <div class="col-3">
                        <button class="btn btn-primary rounded-pill d-flex" @click="addTag()" :disabled="disabled = activeBtn ">
                            <span class="spinner-border spinner-border-sm mt-1 mr-1" role="status" aria-hidden="true" v-if="loading"></span>
                            {{ $t('add') }}
                        </button>
                    </div>
                </div>
            </div>
            <base-table
                fill
                :fill-offset="48"
                :columns="columns"
                :items="filteredList"
                :loading="firstLoading"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                no-local-sorting
                :empty-text="$t('no-results-found')"
            >
                <template #cell(contacts)="{ item }">
                    {{ item.attributes['contacts-count'] || 0 }}
                </template>
                <template #cell(name)="{ item, index }">
                    <div class="form-group m-0 d-flex align-items-center">
                        <input
                            :id="`edit-tag-${item.id}`"
                            type="text"
                            :name="`tag-${item.id}`"
                            v-validate="'required'"
                            class="form-control edit-menu text-center mx-0"
                            :class="{ 'is-invalid': errors.has(`tag-${item.id}`) }"
                            v-model="item.attributes.name"
                            @blur="updateTag(index, item.id, item.attributes.name)"
                            @keyup.enter="$event.target.blur()"
                            @focus="old = item.attributes.name">
                        <label :for="`edit-tag-${item.id}`" class="base-table-action mb-0" :aria-label="$t('edit')">
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" />
                        </label>
                    </div>
                </template>
                <template #cell(actions)="{ item, index }">
                    <button
                        type="button"
                        class="base-table-action base-table-action-danger"
                        :aria-label="$t('delete')"
                        @click="removeTag(index, item.id, item.attributes.name)"
                    >
                        <font-awesome-icon :icon="['fas', 'times']" />
                    </button>
                </template>
            </base-table>
        </div>
    </div>
</template>
<script>

// Internal repository
import Alert                from '../../../helpers/Alert.js';
import BaseTable            from '../BaseTable.vue';

// Import external libraries
import { mapGetters, mapActions } from 'vuex';
import _                          from 'underscore';

// Import Really Fast Deep Copy
const clone = require('rfdc')();

export default {
    components: { BaseTable },

    created () {
        // Show the preloader.
        this.firstLoading = true;

        // Get the tags
        this.getTags()
            .then(() => {
                // Hide the preloader.
                this.firstLoading = false;
            });
    },

    data () {
        return {
            old:          '',
            loading:      false,
            newTag:       '',
            search:       '',
            sortBy:       'name',
            sortDesc:     false,
            firstLoading: false,
            disabled:     true
        };
    },

    computed: {
        ...mapGetters('core/tags', { tags: 'tags' }),

        /**
         * Search tag in the list
         */
        columns () {
            return [
                {
                    key:      'name',
                    label:    this.$t('tags'),
                    sortable: true,
                    thStyle:  { width: '33.33%' },
                    thClass:  'text-left',
                    tdClass:  'tags text-left'
                },
                {
                    key:      'contacts',
                    label:    this.$t('contacts-id'),
                    sortable: true,
                    thStyle:  { width: '33.33%' },
                    thClass:  'text-center',
                    tdClass:  'text-primary text-center'
                },
                {
                    key:     'actions',
                    label:   '',
                    thStyle: { width: '33.33%' },
                    tdClass: 'cell-actions'
                }
            ];
        },

        filteredList () {
            // Generates a filtered array from the tag list
            const list       = clone(_.filter(this.tags, tag => tag.attributes.name.toLowerCase().includes(this.search.toLowerCase())));
            const byName     = (a, b) => a.attributes.name.localeCompare(b.attributes.name, this.$i18n.locale, { numeric: true, sensitivity: 'base' });
            const byContacts = (a, b) => (a.attributes['contacts-count'] || 0) - (b.attributes['contacts-count'] || 0);

            // Sort on the selected column, falling back to the name when the contacts count is equal
            list.sort(this.sortBy === 'contacts' ? (a, b) => byContacts(a, b) || byName(a, b) : byName);

            return this.sortDesc ? list.reverse() : list;
        },

        /**
         * Change button status
         */
        activeBtn () {
            return this.newTag === '';
        }
    },

    methods: {
        ...mapActions('core/tags', {
            getTags:   'index',
            createTag: 'create',
            patchTag:  'update',
            deleteTag: 'delete'
        }),

        /**
         * Detele selected tag
         *
         * @param   {integer}   position    The position of tag in contact tags table.
         * @param   {integer}   tagId       The id of the tag.
         * @param   {string}    tagName     The name of the tag.
         *
         */
        removeTag (position, tagId, tagName) {
            this.$dialog.confirm({
                title:       this.$t('title-tags-delete'),
                subject:     this.$t('confirm-tags-delete'),
                text:        tagName,
                centerText:  true,
                isTextPill:  true,
                confirmText: this.$t('delete'),
                danger:      true,
                onConfirm:   () => this.deleteTag({ id: tagId })
            })
                .then((confirmed) => {
                    if (confirmed) {
                        Alert.success(this.$i18n.t('tag-deleted-success'));
                    }
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                });
        },

        /**
         * Update selected tag
         *
         * @param   {integer}   tagId       The id of the tag.
         * @param   {string}    tagName     The name of the tag.
         */
        updateTag (position, tagId, tagName) {
            // Check if the newTag exist
            if (_.filter(this.tags, tag => tag.id !== tagId).find(tag => tag.attributes.name === tagName)) {
                Alert.fail(this.$i18n.t('tags-create-error'));

                // Refocus the input concerned
                this.$el.querySelector(`#edit-tag-${tagId}`).focus();
                return;
            }

            // Check if tag input empty or unchanged
            if (!tagName.length || this.old === tagName) {
                return;
            }

            // Update tag in db
            this.patchTag({
                id:   tagId,
                name: tagName
            })
                .then(() => Alert.success(this.$i18n.t('information-updated')));
        },

        /**
         * Create tag
         */
        addTag () {
            // Check if the newTag exist
            if (_.find(this.tags, tag => tag.attributes.name === this.newTag)) {
                Alert.fail(this.$i18n.t('tags-create-error'));

                // Clear input
                this.newTag = '';
                return;
            }

            // Spinner on
            this.loading = true;
            this.disabled = true;

            // Create the new tag in db and update tags array
            this.createTag({ name: this.newTag }).then(() => {
                // Spinner off
                this.loading = false;

                // Empty the input new tag
                this.newTag = '';

                // Get the new list of tags
                this.getTags();

                // Success message
                Alert.success(this.$i18n.t('tag-created-success'));
            })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                });
        }
    }
};
</script>
